import React, { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field, useForm, useFormState } from 'react-final-form';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Button, FormCustomSelect, FormField, FormPillSelector, Input } from 'components';
import { actions, duck } from 'coupon-common/src/modules/locations';
import { getNormalizedEntity, required } from 'coupon-common/src/utils';

import messages from './messages';

const CouponLocationField = ({ dispatch, locations, intl, businessInfo, editMode }) => {
  const form = useForm();
  const businessCity = businessInfo?.city;
  const businessAdditionalCities = businessInfo?.additional_cities;
  const initialized = useRef(false);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const provincesKey = duck.provincesEntity.key;

  useEffect(() => {
    dispatch(actions.getProvinces());
    dispatch(actions.getCities());
  }, [dispatch]);

  const {
    entityList: provinceOptions,
    isFetching: provincesFetching,
    error: provincesLoadingError,
  } = getNormalizedEntity(
    locations,
    provincesKey,
    intl,
    messages.entityProvinces || { defaultMessage: 'Provinces' }
  );

  const cityOptions = Object.values(locations?.entities?.cities || {});

  const {
    values = {},
  } = useFormState({
    subscription: { values: true },
  });
  const {province, cities, searchCities = ''} = values;

  // Filter cityOptions based on province and search string
  const filteredCityOptions = useMemo(() => {
    if (!province?.id) return [];

    const provinceCities = cityOptions.filter(city => city.province === province.id);

    if (!searchCities) return provinceCities;

    const lowerSearch = searchCities.toLowerCase();

    return provinceCities.filter(city =>
      city.name?.toLowerCase().includes(lowerSearch)
    );
  }, [province, cityOptions, searchCities]);

  // To pre-fill the city where the business belongs
  useEffect(() => {
    if (!cities?.length || !provinceOptions?.length) {
      if (selectedProvinces.length !== 0) {
        setSelectedProvinces([]); // Only update if it's not already []
      }
      return;
    }

    const selectedProv = provinceOptions.filter((prov) =>
      cities.some((city) => city.province === prov.id)
    );
    setSelectedProvinces(selectedProv);

    // Only change the province on initial load when user edits a coupon
    if (selectedProv?.length > 0 && !province) {
      const firstSelectedProvince = selectedProv[0];
      form.change('province', firstSelectedProvince);
    }
  }, [cities]);

  // Autofill the user's primary and additional cities
  useEffect(() => {
  if (
    !editMode &&
    businessCity &&
    businessAdditionalCities &&
    !cities
  ) {
    form.change('cities', [businessCity, ...businessAdditionalCities]);
  }
}, [businessCity, businessAdditionalCities, values]);

  const handleProvinceClick = (prov) => {
      const filteredCities = cities?.filter((city)=> city?.province !== prov?.id);
      form.change('cities', filteredCities)
  }

  return (
    <div>
      <Field
        name="province"
        label={intl.formatMessage(messages.provinceFieldLabel || { defaultMessage: 'Province' })}
        placeholder={intl.formatMessage(messages.provinceFieldPlaceholder || { defaultMessage: 'Select a province' })}
        validate={required}
        component={FormCustomSelect}
        fetching={provincesFetching}
        options={provinceOptions}
        externalError={provincesLoadingError}
      />
      {province && (


        <div>
          <p className="text-muted m-0">
            {intl.formatMessage(messages.entitySelectedProvinces)}
          </p>

          <div className='d-flex flex-wrap'
            style={{
              maxHeight: '10rem',
              overflowY: 'auto',
              border: 'none',
              padding: '5px',
            }}>
            {
              selectedProvinces?.map((prov) => {
                return (
                  <div key={prov?.id}>
                    <Button
                      checkable
                      checked
                      onClick={()=> handleProvinceClick(prov)}
                    >
                      {`${prov?.name} x`}
                    </Button>
                  </div>
                )
              })
            }
          </div>
        </div>
      )}

      {province && (
        <>
          <h6>{intl.formatMessage(messages.cityBlockHeading || { defaultMessage: 'Select Cities' })}</h6>
          <p className="text-muted">
            {intl.formatMessage(messages.cityInstruction || { defaultMessage: 'Choose one or more cities.' })}
          </p>
          <Field
            name="searchCities"
            placeholder={'Search for a city'}
            readOnly={false}
            component={FormField}
          />
          <div
            style={{
              maxHeight: '15rem',
              overflowY: 'auto',
              border: 'none',
              padding: '5px',
            }}
          >
            <Field
              name="cities"
              type="select"
              component={FormPillSelector}
              options={filteredCityOptions}
              validate={required}
              showSelectAll
            />
          </div>
        </>
      )}
    </div>
  );
};

CouponLocationField.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  locations: PropTypes.object,
  businessInfo: PropTypes.object,
};

const mapStateToProps = (state) => ({
  locations: state.locations,
  businessInfo: state.businessInfo.data,
});

export default connect(mapStateToProps)(injectIntl(CouponLocationField));
